
// Import react
import React, { useState, useEffect } from "react";

// Import states, contexts, and CSS
import { useActiveUser } from "../../store/activeUserContext";
import { SpinnerGeneric } from "../../utils/general/spinners";
import {
  Card,
  Form,
  Row,
  Col,
  Button,
  Table,
} from "react-bootstrap";
import {
  PlusCircle,
  XCircle,
  ArrowClockwise,
} from "react-bootstrap-icons";

// Importa ExcelJS
import ExcelJS from "exceljs";

// Import components
import MachineryListRow from "./machineryListRow";

const MachineryListComponent = () => {

  const { activeUser } = useActiveUser();
  const userId = activeUser._id;
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [machineryList, setMachineryList] = useState([]);
  const [newMachinery, setNewMachinery] = useState({
    machineryType: "",
    machineryName: "",
  });

  const [importFile, setImportFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now()); // Stato per la key dell'input file

  const [filter, setFilter] = useState("");

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };

  const handleFileReset = () => {
    setImportFile(null); // Resetta lo stato del file
    setFileInputKey(Date.now()); // Cambia la key per forzare un re-render dell'input
  };

  const handleImportFile = async () => {
    if (!importFile) return;

    const formData = new FormData();
    formData.append("file", importFile);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/machinery/users/${activeUser._id}/machinery/import`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        alert(`${result.message}`);
        setUpdate((prev) => !prev); // aggiorna la lista
        setImportFile(null);
        handleFileReset();
      } else {
        alert("Errore nell'importazione");
      }
    } catch (error) {
      console.error("Errore import:", error);
    }
  };

  const handleChange = (e) => {
    setNewMachinery({ ...newMachinery, [e.target.name]: e.target.value });
  };

  const handleAddMachinery = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/api/machinery/users/${userId}/machinery`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(newMachinery),
        }
      );

      if (response.ok) {
        const savedMachinery = await response.json();
        setMachineryList([...machineryList, savedMachinery]);
        setNewMachinery({ machineryType: "", machineryName: "" });
        alert(savedMachinery.message || "Macchina aggiunta con successo!");
      } else {
        console.error("Errore durante l'aggiunta della macchina");
        alert("Errore durante l'aggiunta della macchina.");
      }
    } catch (error) {
      console.error("Errore durante la richiesta POST:", error);
      alert("Errore durante la connessione al server.");
    } finally {
      setIsLoading(false);
    }
  };

  // Funzione per scaricare il modello Excel
  const downloadTemplate = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Machinery Template");

    // Aggiungi le intestazioni delle colonne
    worksheet.columns = [
      { header: "Machinery Name", key: "machineryName", width: 30 },
      { header: "Machinery Type", key: "machineryType", width: 30 },
    ];

    // Aggiungi una riga vuota di esempio (opzionale)
    worksheet.addRow({ machineryName: "", machineryType: "" });

    // Impostazione per scaricare il file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "machinery_template.xlsx";
      a.click();
      URL.revokeObjectURL(url); // Libera la memoria dopo il download
    });
  };

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const fetchMachinery = async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/api/machinery/users/${activeUser._id}/machinery?filter=${filter}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const data = await response.json();
          if (response.ok) {
            console.log("Macchinari ricevuti:", data);
            setMachineryList(data);
          }
        } catch (error) {
          console.error("Error fetching machinery:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchMachinery();
    }, 1500);

    return () => clearTimeout(timer);
  }, [filter, activeUser._id, token, update]);

  const handleRefresh = () => {
    setUpdate((prev) =>!prev);
    setFilter("");
  }

  return (
    <div>
      {/* User Info */}
      <Row className="mb-4">
        <Col>
          {activeUser ? (
            <>
              <p>
                <strong>Utente ID:</strong> {activeUser._id}
              </p>
              <p>
                <strong>Nome:</strong> {activeUser.firstName}
              </p>
            </>
          ) : (
            <p>Nessun utente registrato.</p>
          )}
        </Col>
      </Row>

      {/* Import from Excel/CSV */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <Card.Title className="mb-0">Import Machinery</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form.Group controlId="fileUpload" className="mb-3">
            <Form.Label>Select Excel or CSV file</Form.Label>
            <Form.Control
              key={fileInputKey}
              type="file"
              accept=".csv, .xlsx"
              onChange={handleFileChange}
            />
          </Form.Group>
          <Button variant="primary" size="sm" onClick={handleImportFile}>
            <PlusCircle className="me-2" size={16} />
            Import File
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="ms-3"
            onClick={downloadTemplate}
          >
            <XCircle className="me-2" size={16} />
            Download Template
          </Button>
        </Card.Body>
      </Card>

      {/* Add New Machinery */}
      <Card className="mb-4 shadow-sm">
        <Card.Header>
          <Card.Title className="mb-0">Add New Machinery</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row className="align-items-center mb-3">
              <Col md={4}>
                <Form.Label>Machinery Type</Form.Label>
              </Col>
              <Col md={8}>
                <Form.Control
                  type="text"
                  placeholder="Enter machinery type"
                  name="machineryType"
                  value={newMachinery.machineryType}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            <Row className="align-items-center mb-3">
              <Col md={4}>
                <Form.Label>Machinery Name</Form.Label>
              </Col>
              <Col md={8}>
                <Form.Control
                  type="text"
                  placeholder="Enter machinery name"
                  name="machineryName"
                  value={newMachinery.machineryName}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            <Button variant="success" size="sm" onClick={handleAddMachinery }
            disabled={newMachinery.machineryType ==="" || newMachinery.machineryName === ""}
            >
              <PlusCircle className="me-2" size={16} />
              Add Machinery
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Machinery Table */}
      <Card className="mb-5 shadow-sm">
        <Card.Header>
          <Card.Title className="mb-0">Machinery Table</Card.Title>
        </Card.Header>
        <Card.Body>
          <Form.Group controlId="searchInput" className="mb-3">
            <Form.Label>Search</Form.Label>
            <div className="d-flex">
              <Form.Control
                type="text"
                placeholder="Search for machinery..."
                value={filter}
                onChange={handleFilterChange}
                className="me-2" // Margine destro per separare dal pulsante
              />
              <Button variant="secondary" onClick={handleRefresh}><ArrowClockwise/></Button>
            </div>
          </Form.Group>
          {/* Machinery List */}
          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th>Machinery ID</th>
                <th>Type</th>
                <th>Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    <SpinnerGeneric />
                  </td>
                </tr>
              ) : machineryList.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    Nessun macchinario trovato
                  </td>
                </tr>
              ) : (
                machineryList.map((machinery, index) => (
                  <MachineryListRow
                    key={index}
                    index={index}
                    machinery={machinery}
                    setUpdate={setUpdate}
                    setFilter={setFilter}
                  />
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default MachineryListComponent;
